import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, Row } from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import { getRecords, saveRecords } from "../../services/api.service.js";
import { loaderOverlay } from "../../Style/loaderOverly.js";
import { LoaderCreatePage } from "../Loader/Loader.js";
import TextInput from "../../custom/components/Text-Input/TextInput.js";
import SelectInput from "../../custom/components/Select-Input/SelectInput.js";
import DatePicker from "../../custom/components/Date-Picker/DatePicker.js";
import { validate } from "../../custom/validations/Validate.js";
import CourierInvoiceDetails from "./CourierInvoiceDetails.js";
import {
  basicFormData,
  createPageData,
  COURIER_PENDING,
  COURIER_COMPLETED,
} from "./CourierFile.js";

// the native date input only understands YYYY-MM-DD
const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const CourierCreate = () => {
  const { id } = useParams(); // outgoing invoice id, present when editing
  const navigate = useNavigate();

  const {
    payload,
    navigatePage,
    headerName,
    validationType,
    createEndpoint,
    updateEndpoint,
    invoiceViewEndpoint,
  } = createPageData();
  const initialPayload = useMemo(() => payload, []);

  const [formData, setFormData] = useState(basicFormData());
  const [formErrors, setFormErrors] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null); // { invoice, dispatches }

  const isEdit = Boolean(id);
  // courier details can only be submitted once - if this invoice's courier
  // details are already in (status 2), the form is view-only even when
  // reached directly via /courier-create/:id
  const isAlreadySubmitted =
    isEdit && invoiceDetails?.invoice?.status === COURIER_COMPLETED;

  useEffect(() => {
    if (isEdit) {
      // editing an existing courier entry - the invoice is already decided
      loadInvoiceDetails(id, { prefillCourier: true });
    } else {
      getPendingInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* Only invoices that are still waiting for courier details can be picked. */
  const getPendingInvoices = async () => {
    setLoadingData(true);
    try {
      const responseData = await getRecords(
        {
          modelName: "outgoing_invoices",
          schema: localStorage.getItem("schema"),
          whereCondition: {
            is_active: true,
            status: COURIER_PENDING,
          },
          orderBy: {
            field: "invoice_number",
            order: "asc",
          },
        },
        "/getRecords",
      );

      setInvoiceOptions(
        (responseData?.data || []).map((invoice) => ({
          value: invoice?.id,
          label: invoice?.invoice_number,
        })),
      );
    } catch (error) {
      console.error("Error fetching outgoing invoices:", error);
      toast.error("Something went wrong! Please try again");
      setInvoiceOptions([]);
    } finally {
      setLoadingData(false);
    }
  };

  const loadInvoiceDetails = async (invoiceId, { prefillCourier } = {}) => {
    setLoadingData(true);
    try {
      const responseData = await getRecords(
        { invoice_id: invoiceId },
        invoiceViewEndpoint,
      );

      const details = responseData?.data;
      if (!details?.invoice) {
        toast.error("Invoice not found");
        setInvoiceDetails(null);
        return;
      }

      setInvoiceDetails(details);
      setSelectedInvoice({
        value: details.invoice.id,
        label: details.invoice.invoice_number,
      });

      if (prefillCourier) {
        setFormData({
          inputData: {
            courier_name: details.invoice.courier_name || "",
            courier_docket_number: details.invoice.courier_docket_number || "",
            courier_docket_date: toDateInputValue(
              details.invoice.courier_docket_date,
            ),
          },
        });
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      toast.error("Something went wrong! Please try again");
      setInvoiceDetails(null);
    } finally {
      setLoadingData(false);
    }
  };

  const handleInvoiceSelect = (option) => {
    setSelectedInvoice(option);
    setInvoiceDetails(null);
    setFormData(basicFormData());
    setFormErrors((prev) => ({ ...prev, invoice_id: null }));

    if (option?.value) {
      loadInvoiceDetails(option.value);
    }
  };

  const onInputChange = (e) => {
    const { name, value } = e.target;

    setFormErrors((prevErrors) => {
      const fieldError = validate(validationType, { [name]: value });
      return {
        ...prevErrors,
        [name]: fieldError[name] || null,
      };
    });

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        [name]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedInvoice?.value) {
      setFormErrors((prev) => ({
        ...prev,
        invoice_id: "Outgoing Invoice No cannot be blank.",
      }));
      return;
    }

    const validationErrors = validate(validationType, formData.inputData);
    setFormErrors(validationErrors);
    if (JSON.stringify(validationErrors) !== "{}") return;

    setLoadingData(true);
    try {
      const userDetails = JSON.parse(localStorage.getItem("authUser"));

      // courier details are stored on the outgoing invoice row, so this is
      // always an update against the selected invoice id
      const requestPayload = {
        modelName: initialPayload?.modelName,
        schema: initialPayload?.schema,
        id: selectedInvoice.value,
        inputData: {
          ...formData.inputData,
          updated_by: userDetails?.id || "",
        },
      };

      const responseData = await saveRecords(
        requestPayload,
        isEdit ? updateEndpoint : createEndpoint,
      );

      if (responseData && responseData.code === 1) {
        toast.success(
          `${headerName} details ${isEdit ? "updated" : "saved"} successfully!`,
        );
        navigate(navigatePage?.back);
      } else {
        toast.error(
          responseData?.message || `Error while saving ${headerName} details!`,
        );
      }
    } catch (error) {
      console.error("Error during submission:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="d-flex align-items-center">
          <Breadcrumbs
            title={isEdit ? "Update Courier Details" : "Add Courier Details"}
            breadcrumbItems={[]}
          />
          <Button
            color="secondary"
            className="waves-effect waves-light me-1 ms-auto"
            onClick={() => navigate(navigatePage?.back)}
          >
            Back
          </Button>
        </div>

        <div className="card mb-3">
          <div className="container-fluid p-4">
            <p className="text-muted pb-1">
              Fields marked with <span className="text-danger fw-bold">*</span>{" "}
              are required.
            </p>

            <Row>
              <Col md="6">
                <SelectInput
                  label="Outgoing Invoice No"
                  options={invoiceOptions}
                  value={selectedInvoice}
                  onSelect={handleInvoiceSelect}
                  placeholder="Select Outgoing Invoice No"
                  noDataMessage="No invoice pending for courier"
                  labelClassName="form-label"
                  isClearable={!isEdit}
                  disabled={isEdit}
                  asterisk={true}
                  customError={formErrors?.invoice_id}
                />
              </Col>
            </Row>
          </div>

          {loadingData && (
            <div style={loaderOverlay}>
              <LoaderCreatePage />
            </div>
          )}
        </div>

        {invoiceDetails?.invoice && (
          <div className="card mt-2 p-3">
            <CourierInvoiceDetails
              invoice={invoiceDetails?.invoice}
              dispatches={invoiceDetails?.dispatches}
            />

            <h5 className="fw-bold mb-2 mt-2 border-top pt-3">
              Courier Details
            </h5>

            {isAlreadySubmitted && (
              <div className="alert alert-info" role="alert">
                Courier details have already been submitted for this invoice
                and cannot be updated.
              </div>
            )}

            <Row>
              <Col md="4" className="mt-3">
                <TextInput
                  label="Courier Name"
                  name="courier_name"
                  value={formData?.inputData?.courier_name}
                  onChange={onInputChange}
                  placeholder="Enter Courier Name"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  asterisk={true}
                  customError={formErrors?.courier_name}
                  disabled={isAlreadySubmitted}
                />
              </Col>

              <Col md="4" className="mt-3">
                <TextInput
                  label="Courier Docket No"
                  name="courier_docket_number"
                  value={formData?.inputData?.courier_docket_number}
                  onChange={onInputChange}
                  placeholder="Enter Courier Docket No"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  asterisk={true}
                  customError={formErrors?.courier_docket_number}
                  disabled={isAlreadySubmitted}
                />
              </Col>

              <Col md="4" className="mt-3">
                <DatePicker
                  label="Courier Docket Date"
                  name="courier_docket_date"
                  value={formData?.inputData?.courier_docket_date}
                  onChange={onInputChange}
                  placeholder="Courier Docket Date"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  asterisk={true}
                  customError={formErrors?.courier_docket_date}
                  disabled={isAlreadySubmitted}
                />
              </Col>
            </Row>

            <div className="mt-3 d-flex gap-3 flex-wrap">
              {!isAlreadySubmitted && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn btn-primary"
                >
                  Submit
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate(navigatePage?.back)}
                className="btn btn-secondary"
              >
                {isAlreadySubmitted ? "Back" : "Cancel"}
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default CourierCreate;
